/**
 * Helpers for the cloud data-table image gallery.
 *
 * Left/right stays inside one row's images. Up/down walks the currently
 * loaded rows, skipping any that have no images for the same column context
 * the gallery was opened from. Pagination beyond the loaded page is ignored.
 */

export const TITLE_HINTS = ["name", "title", "product_name", "product name", "product"];

const isPreviewColumn = (column) =>
  column?.id === "image_group_toggle" ||
  column?.id === "image_previews" ||
  column?.type === "image_group_toggle" ||
  column?.type === "image_previews";

/**
 * Collect the image URLs a gallery should show for a row, matching the
 * column the user clicked (combined preview vs a single image column).
 *
 * @param {Object} item Table row (`{ data }`)
 * @param {Object} column Column the gallery was opened from
 * @param {Array} columns Display columns (fallback for preview imageColumns)
 * @param {Function} expandImageUrls (value) => string[]
 * @returns {string[]}
 */
export const collectRowGalleryImages = (item, column, columns, expandImageUrls) => {
  if (!item?.data || typeof expandImageUrls !== "function") return [];

  if (isPreviewColumn(column)) {
    const imageColumns =
      column.imageColumns ||
      (columns || []).filter((col) => col.type === "image" && col.id !== "image_previews");
    return imageColumns.flatMap((col) => expandImageUrls(item.data[col.id]) || []);
  }

  if (!column?.id) return [];
  return expandImageUrls(item.data[column.id]) || [];
};

/**
 * Walk loaded rows in `direction` (-1 up / +1 down) until a row has images.
 *
 * @param {Array} rows Currently loaded / visible rows
 * @param {string|number} currentRowId
 * @param {number} direction -1 for previous, +1 for next
 * @param {Function} getImages (row) => string[]
 * @returns {{ row: Object, images: string[], rowIndex: number } | null}
 */
export const findAdjacentGalleryRow = (rows, currentRowId, direction, getImages) => {
  if (!Array.isArray(rows) || rows.length === 0 || currentRowId == null) return null;
  if (typeof getImages !== "function") return null;

  const step = direction < 0 ? -1 : 1;
  const start = rows.findIndex((row) => String(row.id) === String(currentRowId));
  if (start === -1) return null;

  for (let i = start + step; i >= 0 && i < rows.length; i += step) {
    const images = getImages(rows[i]) || [];
    if (images.length > 0) {
      return { row: rows[i], images, rowIndex: i };
    }
  }
  return null;
};

/**
 * Keep the current image slot when changing rows, clamped to the new row.
 *
 * @param {number} index
 * @param {number} imageCount
 * @returns {number}
 */
export const clampGalleryImageIndex = (index, imageCount) => {
  if (!imageCount || imageCount < 1) return 0;
  if (index == null || Number.isNaN(index) || index < 0) return 0;
  return Math.min(Math.floor(index), imageCount - 1);
};

/**
 * Best-effort label for the open gallery row (product name, else "Row N").
 *
 * @param {Object} row
 * @param {Array} displayColumns
 * @returns {string}
 */
export const resolveRowGalleryTitle = (row, displayColumns = []) => {
  if (!row?.data) {
    return row?.displayIndex != null ? `Row ${row.displayIndex}` : "Image Gallery";
  }

  const matchCol = (displayColumns || []).find((col) => {
    const id = String(col.id || "").toLowerCase();
    const name = String(col.name || "").toLowerCase();
    return TITLE_HINTS.includes(id) || TITLE_HINTS.includes(name);
  });
  if (matchCol) {
    const value = row.data[matchCol.id];
    if (typeof value === "string" && value.trim()) return value.trim();
  }

  for (const [key, value] of Object.entries(row.data)) {
    if (TITLE_HINTS.includes(String(key).toLowerCase()) && typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return row.displayIndex != null ? `Row ${row.displayIndex}` : "Image Gallery";
};
